import React, { useRef } from 'react';
import {
  Box, Accordion, AccordionSummary, AccordionDetails,
  Typography, TextField, Button, IconButton, Divider,
  Chip, Stack, Tooltip, Paper,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as MoveUpIcon,
  ArrowDownward as MoveDownIcon,
  RestartAlt as ResetIcon,
  Image as ImageIcon,
  PhotoCamera as CameraIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { MP } from './manualPalette';

const sx_input = {
  '& .MuiOutlinedInput-root': {
    bgcolor: alpha(MP.white, 0.05),
    color: MP.textLight,
    '& fieldset': { borderColor: alpha(MP.white, 0.15) },
    '&:hover fieldset': { borderColor: alpha(MP.yellow, 0.5) },
    '&.Mui-focused fieldset': { borderColor: MP.yellow },
  },
  '& .MuiInputLabel-root': { color: MP.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: MP.yellow },
};

const ManualEditor = ({ content, imageOverrides, actions }) => {
  const {
    updateMeta, updateSection, updateStepItem,
    addStep, removeStep, moveStep,
    updateImage, removeImageOverride, resetChapter,
  } = actions;

  return (
    <Box>
      {/* Meta editor */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 3,
          bgcolor: MP.cardBg,
          border: `1px solid ${alpha(MP.yellow, 0.25)}`,
          borderRadius: 2,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: MP.yellow }}>
          📄 Document Header
        </Typography>
        <Stack spacing={2}>
          <TextField label="Document Title" value={content.meta.title}
            onChange={(e) => updateMeta('title', e.target.value)} fullWidth size="small" sx={sx_input} />
          <TextField label="Subtitle" value={content.meta.subtitle}
            onChange={(e) => updateMeta('subtitle', e.target.value)} fullWidth size="small" sx={sx_input} />
          <Stack direction="row" spacing={2}>
            <TextField label="Version" value={content.meta.version}
              onChange={(e) => updateMeta('version', e.target.value)} size="small" sx={{ flex: 1, ...sx_input }} />
            <TextField label="Last Updated" value={content.meta.lastUpdated}
              onChange={(e) => updateMeta('lastUpdated', e.target.value)} size="small" sx={{ flex: 2, ...sx_input }} />
          </Stack>
        </Stack>
      </Paper>

      {content.chapters.map((chapter, chIdx) => (
        <ChapterEditor
          key={chapter.id}
          chapter={chapter}
          chapterIndex={chIdx}
          imageOverrides={imageOverrides}
          onUpdateSection={updateSection}
          onUpdateStepItem={updateStepItem}
          onAddStep={addStep}
          onRemoveStep={removeStep}
          onMoveStep={moveStep}
          onUpdateImage={updateImage}
          onRemoveImageOverride={removeImageOverride}
          onReset={() => resetChapter(chapter.id)}
        />
      ))}
    </Box>
  );
};

const ChapterEditor = ({
  chapter, chapterIndex, imageOverrides,
  onUpdateSection, onUpdateStepItem, onAddStep, onRemoveStep, onMoveStep,
  onUpdateImage, onRemoveImageOverride, onReset,
}) => (
  <Accordion
    defaultExpanded={chapterIndex === 0}
    sx={{
      mb: 1.5,
      bgcolor: MP.cardBg,
      border: `1px solid ${alpha(MP.white, 0.08)}`,
      borderRadius: '8px !important',
      '&::before': { display: 'none' },
      boxShadow: 'none',
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon sx={{ color: MP.yellow }} />}
      sx={{
        bgcolor: alpha(MP.deepBlue, 0.6),
        borderRadius: 2,
        '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1.5 },
      }}
    >
      <Chip
        label={`Ch. ${chapterIndex + 1}`}
        size="small"
        sx={{ bgcolor: MP.yellow, color: MP.black, fontWeight: 800, minWidth: 56 }}
      />
      <Typography fontWeight={600} sx={{ color: MP.white }}>{chapter.title}</Typography>
      <Box sx={{ ml: 'auto', mr: 1 }}>
        <Tooltip title="Reset chapter to default">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onReset(); }}
            sx={{ color: MP.yellow }}>
            <ResetIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 2 }}>
      {chapter.sections.map((section) => (
        <SectionEditor
          key={section.id}
          section={section}
          chapterId={chapter.id}
          imageOverride={imageOverrides[section.id]}
          onUpdateSection={onUpdateSection}
          onUpdateStepItem={onUpdateStepItem}
          onAddStep={onAddStep}
          onRemoveStep={onRemoveStep}
          onMoveStep={onMoveStep}
          onUpdateImage={onUpdateImage}
          onRemoveImageOverride={onRemoveImageOverride}
        />
      ))}
    </AccordionDetails>
  </Accordion>
);

const SectionEditor = ({
  section, chapterId, imageOverride,
  onUpdateSection, onUpdateStepItem, onAddStep, onRemoveStep, onMoveStep,
  onUpdateImage, onRemoveImageOverride,
}) => {
  const fileInputRef = useRef(null);
  const items = section.steps || section.items;
  const currentImage = imageOverride || section.image;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.onload = () => {
        const MAX_W = 1200;
        const scale = img.width > MAX_W ? MAX_W / img.width : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressed = canvas.toDataURL('image/jpeg', 0.75);
        const caption = imageOverride?.caption || section.image?.caption || '';
        onUpdateImage(section.id, compressed, caption);
      };
      img.src = ev.target.result;
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  return (
    <Box sx={{ mb: 3, pl: 1 }}>
      <Typography variant="subtitle2" sx={{
        mb: 1.5, color: MP.greenLight, fontWeight: 700,
        display: 'flex', alignItems: 'center', gap: 1,
      }}>
        {section.title}
        {imageOverride && (
          <Chip label="Custom Image" size="small"
            sx={{ bgcolor: MP.yellow, color: MP.black, fontSize: '0.62rem', height: 18, fontWeight: 700 }} />
        )}
      </Typography>

      {section.type === 'body' && (
        <TextField multiline minRows={3} maxRows={10} fullWidth
          value={section.body}
          onChange={(e) => onUpdateSection(chapterId, section.id, 'body', e.target.value)}
          size="small" placeholder="Section body text…" sx={{ mb: 1.5, ...sx_input }} />
      )}

      {items && (
        <Box sx={{ mb: 1.5 }}>
          {items.map((item, idx) => (
            <Stack key={idx} direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.75 }}>
              <Chip
                label={section.type === 'steps' ? idx + 1 : '•'}
                size="small"
                sx={{ minWidth: 26, height: 22, fontSize: '0.68rem', fontWeight: 800,
                  bgcolor: section.type === 'steps' ? MP.green : MP.deepBlue, color: MP.yellow }}
              />
              <TextField size="small" value={item}
                onChange={(e) => onUpdateStepItem(chapterId, section.id, idx, e.target.value)}
                fullWidth sx={sx_input} />
              <Tooltip title="Move up"><span>
                <IconButton size="small" disabled={idx === 0}
                  onClick={() => onMoveStep(chapterId, section.id, idx, idx - 1)}
                  sx={{ color: MP.textMuted }}>
                  <MoveUpIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span></Tooltip>
              <Tooltip title="Move down"><span>
                <IconButton size="small" disabled={idx === items.length - 1}
                  onClick={() => onMoveStep(chapterId, section.id, idx, idx + 1)}
                  sx={{ color: MP.textMuted }}>
                  <MoveDownIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </span></Tooltip>
              <Tooltip title="Remove">
                <IconButton size="small" onClick={() => onRemoveStep(chapterId, section.id, idx)}
                  sx={{ color: '#ef5350' }}>
                  <DeleteIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Stack>
          ))}
          <Button size="small" startIcon={<AddIcon />}
            onClick={() => onAddStep(chapterId, section.id)}
            sx={{ mt: 0.5, color: MP.greenLight, borderColor: MP.green, '&:hover': { borderColor: MP.greenLight } }}
            variant="outlined">
            Add {section.type === 'steps' ? 'Step' : 'Item'}
          </Button>
        </Box>
      )}

      {section.tip !== undefined && (
        <TextField size="small" fullWidth label="💡 Tip" value={section.tip}
          onChange={(e) => onUpdateSection(chapterId, section.id, 'tip', e.target.value)}
          sx={{ mb: 1, ...sx_input }} />
      )}
      {section.note !== undefined && (
        <TextField size="small" fullWidth label="📝 Note" value={section.note}
          onChange={(e) => onUpdateSection(chapterId, section.id, 'note', e.target.value)}
          sx={{ mb: 1, ...sx_input }} />
      )}
      {section.warning !== undefined && (
        <TextField size="small" fullWidth label="⚠️ Warning" value={section.warning}
          onChange={(e) => onUpdateSection(chapterId, section.id, 'warning', e.target.value)}
          sx={{ mb: 1, ...sx_input }} />
      )}

      {currentImage && (
        <Paper elevation={0} sx={{
          p: 1.5,
          border: `1px dashed ${imageOverride ? MP.yellow : alpha(MP.white, 0.15)}`,
          borderRadius: 2,
          bgcolor: alpha(imageOverride ? MP.yellow : MP.white, 0.04),
        }}>
          <Typography variant="caption" sx={{ mb: 1, display: 'block', color: MP.textMuted }}>
            <ImageIcon sx={{ fontSize: 13, mr: 0.5, verticalAlign: 'middle' }} />
            Section Image
          </Typography>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <Box component="img" src={currentImage.src} alt={currentImage.caption}
              sx={{ width: 80, height: 58, objectFit: 'cover', borderRadius: 1,
                border: `1px solid ${alpha(MP.white, 0.15)}`, flexShrink: 0 }}
              onError={(e) => { e.target.style.display = 'none'; }} />
            <Stack spacing={1} sx={{ flex: 1 }}>
              <TextField size="small" fullWidth label="Caption"
                value={currentImage.caption || ''}
                onChange={(e) => {
                  if (imageOverride) onUpdateImage(section.id, imageOverride.src, e.target.value);
                  else onUpdateSection(chapterId, section.id, 'image', { ...section.image, caption: e.target.value });
                }}
                sx={sx_input} />
              <Stack direction="row" spacing={1}>
                <input ref={fileInputRef} type="file" accept="image/*"
                  style={{ display: 'none' }} onChange={handleImageUpload} />
                <Button size="small" variant="outlined" startIcon={<CameraIcon />}
                  onClick={() => fileInputRef.current?.click()}
                  sx={{ color: MP.yellow, borderColor: MP.yellow, '&:hover': { borderColor: MP.yellowMid, bgcolor: alpha(MP.yellow, 0.08) } }}>
                  Replace Image
                </Button>
                {imageOverride && (
                  <Button size="small" variant="text" startIcon={<CloseIcon />}
                    onClick={() => onRemoveImageOverride(section.id)}
                    sx={{ color: MP.textMuted }}>
                    Restore Default
                  </Button>
                )}
              </Stack>
            </Stack>
          </Stack>
        </Paper>
      )}
      <Divider sx={{ mt: 2, borderColor: alpha(MP.white, 0.06) }} />
    </Box>
  );
};

export default ManualEditor;
